import React, { useEffect, useState } from 'react';
import { MdLockOutline, MdOutlineTimer } from 'react-icons/md';
import { BiHeart } from 'react-icons/bi';
import { IoIosArrowDown } from 'react-icons/io';
import { IoTicketOutline, IoTimeOutline } from 'react-icons/io5';
import { MdOutlineHealthAndSafety, MdOutlinePhoneIphone } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import cartService from '../api/cart.service';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { toggleWishlistItem } from '../redux/slice/wishlistSlice';
import { FaHeart } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SwapConfirmationModal from './SwapConfirmationModal';

const infoItems = [
  {
    icon: <IoTicketOutline className='w-5 h-5 text-[#79b1d3]' />,
    title: 'Free Cancellation',
    description:
      'Enjoy hassle-free booking with our flexible cancellation policy. For bookings made at least 72 hours before the event, you are eligible for a full refund of the advance payment if you cancel within 24 hours of booking.',
  },
  {
    icon: <MdOutlineHealthAndSafety className='w-5 h-5 text-[#79b1d3]' />,
    title: 'Instant Confirmation',
    description:
      'Receive immediate confirmation of your booking upon successful payment, ensuring your event planning remains seamless and efficient.',
  },
  {
    icon: <MdOutlinePhoneIphone className='w-5 h-5 text-[#79b1d3]' />,
    title: 'Customization',
    description:
      'Make your event uniquely yours! Choose from a wide range of personalized options, including decor themes, catering preferences, and schedule adjustments. Let us bring your vision to life while ensuring every detail aligns perfectly with your needs.',
  },
  {
    icon: <IoTimeOutline className='w-5 h-5 text-[#79b1d3]' />,
    title: 'Duration',
    description: 'The standard event duration is a maximum of 3 hours.',
  },
];

const ProductDetailsPopupWithCount = ({
  id,
  title,
  price,
  image,
  onClose,
  type,
  nextPage,
  items,
  description,
  details,
  cartItems,
  fromCartEdit = false,
  currentItemId = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const eventFormState = useSelector((state) => state.form);
  const [loading, setLoading] = useState(false);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAlreadyInCart, setIsAlreadyInCart] = useState(false);
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [cartItemId, setCartItemId] = useState(null);
  const [swapActive, setSwapActive] = useState(fromCartEdit);
  const [localCartItemId, setLocalCartItemId] = useState(currentItemId);
  const [showSwapConfirmModal, setShowSwapConfirmModal] = useState(false);
  const dispatch = useDispatch();

  const { items: wishlistItems } = useSelector((state) => state.wishlist);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (cartItems) {
      const existingItem = cartItems.find((item) => item.item_id === id);
      if (existingItem) {
        setIsAlreadyInCart(true);
        setQuantity(existingItem.quantity);
        setCartItemId(existingItem.id);
      } else {
        setIsAlreadyInCart(false);
        setQuantity(1);
      }
    }
  }, [id, cartItems]);

  useEffect(() => {
    if (swapActive && localCartItemId && cartItems) {
      const swappedItemExists = cartItems.some(
        (item) => item.id === localCartItemId
      );
      if (!swappedItemExists) {
        setSwapActive(false);
        setLocalCartItemId(null);
      }
    }
  }, [cartItems, swapActive, localCartItemId]);

  const isFavorite = wishlistItems.some(
    (item) => item.item_id === id && item.item_type === type
  );

  const handleWishlist = async (e) => {
    e.stopPropagation();
    if (loading) return;
    if (!user) {
      toast.error('Unauthorized request. Please Sign In');
      return;
    }
    try {
      await dispatch(toggleWishlistItem({ id, type })).unwrap();
      toast.dismiss();
      toast.success(isFavorite ? 'Removed from wishlist' : 'Added to wishlist');
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const handleIncreaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const buildEventDateTime = () =>
    new Date(
      `${eventFormState?.date}T${eventFormState?.eventTime || '00:00'}`
    ).toISOString();

  const addCartItem = async () => {
    setLoading(true);
    try {
      const payload = {
        item_id: id,
        item_type: type,
        quantity,
        event_date: buildEventDateTime(),
      };

      let response;
      if (isAlreadyInCart && cartItemId) {
        response = await cartService.updateCartItem(cartItemId, payload);
      } else {
        response = await cartService.createCartItem(payload);
      }

      if (response.success) {
        toast.success('Cart updated successfully');
        setTimeout(() => {
          window.location.reload();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 10);
      } else {
        toast.error(response.message || 'Failed to update cart');
      }
      nextPage ? navigate('/' + nextPage) : onClose();
    } catch (error) {
      console.log('error: ', error);
      toast.error('Error updating cart');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (swapActive && localCartItemId) {
      if (localCartItemId === cartItemId) {
        toast.error('Please swap any other products, You ');
        return;
      }
      setShowSwapConfirmModal(true);
      return;
    }
    await addCartItem();
  };

  const handleSwapConfirm = async () => {
    setShowSwapConfirmModal(false);
    try {
      const delResponse = await cartService.deleteCartItem(localCartItemId);
      if (delResponse.success) {
        toast.success('Existing item removed.');
        setSwapActive(false);
        setLocalCartItemId(null);
        await addCartItem();
        navigate('/cart');
      } else {
        toast.error(delResponse.message || 'Failed to remove existing item');
      }
    } catch (error) {
      toast.error('Error during swap.');
    }
  };

  const handleRemoveFromCart = async () => {
    if (!cartItemId) {
      toast.error('Cart item not found');
      return;
    }
    setIsRemoveLoading(true);
    try {
      const response = await cartService.deleteCartItem(cartItemId);
      if (response.success) {
        toast.success('Removed from cart');
        setTimeout(() => {
          window.location.reload();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 10);
      } else {
        toast.error(response.message || 'Failed to remove from cart');
      }
      nextPage ? navigate('/' + nextPage) : onClose();
    } catch (error) {
      toast.error('Error removing from cart');
    } finally {
      setIsRemoveLoading(false);
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setQuantity(value);
    }
  };

  const handleQuantityBlur = () => {
    setIsEditingQuantity(false);
  };

  const accordionVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: { duration: 0.3 },
    },
  };

  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    hover: { scale: 1.1, rotate: 5 },
    tap: { scale: 1.4, rotate: 20 },
  };

  const textVariants = {
    initial: { opacity: 1, x: 0 },
    tap: { opacity: 0.8, x: -5 },
  };

  return (
    <>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-2 bg-black bg-opacity-10'>
        <div className='relative w-full max-w-4xl h-[92vh] md:px-6 p-2 md:py-4 py-3 bg-mainbg rounded-lg shadow-xl overflow-hidden overflow-y-auto'>
          <div className='relative md:p-8 p-2 pt-8'>
            <button
              onClick={onClose}
              className='absolute right-2 top-2 text-gray-600 hover:text-gray-800'
            >
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
            <h1 className='md:text-2xl text-lg font-bold text-center mb-6 text-textDarkPrimary'>
              {title}
            </h1>
          </div>

          <div className='grid md:grid-cols-2 gap-6 px-2'>
            <div className='space-y-4'>
              {image && (
                <div className='bg-gray-100 rounded-lg overflow-hidden'>
                  <img
                    src={image.url}
                    alt={title}
                    className='w-full aspect-square object-cover'
                  />
                </div>
              )}

              {infoItems.length > 0 && (
                <div className='grid md:grid-cols-2 grid-cols-1 bg-gray-50 border-[#d1e1ea] border-2'>
                  {infoItems.map((item, index) => (
                    <div
                      key={index}
                      className='flex flex-col border-b-2 border-[#c5d4dd] p-2 bg-[#DAE7ED]'
                    >
                      <div className='flex items-center gap-2 mb-2'>
                        {item.icon}
                        <span className='text-xs font-bold text-textprimary'>
                          {item.title}
                        </span>
                      </div>
                      <p className='text-xs text-gray-600'>
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='space-y-4'>
              <div className='p-3 bg-[#d7e2e9] border border-buttonprimary'>
                {price && (
                  <div className='flex justify-between items-center mb-1'>
                    <span className='md:text-lg font-medium '>Price</span>
                    <span className='md:text-xl font-bold text-textprimary'>
                      ₹ {price}
                    </span>
                  </div>
                )}
                <div className='h-[1px] bg-gray-300 mb-2' />
                {items && items.length > 0 && (
                  <ul className='space-y-1 mb-3'>
                    {items.map((item, index) => {
                      if (
                        typeof item === 'object' &&
                        item !== null &&
                        item.name &&
                        item.quantity !== undefined
                      ) {
                        return (
                          <li key={index} className='flex items-center gap-2'>
                            <div className='w-1 h-1 bg-gray-700 rounded-full'></div>
                            <span className='text-sm'>
                              {item.name} - {item.quantity}
                            </span>
                          </li>
                        );
                      } else {
                        return (
                          <li key={index} className='flex items-center gap-2'>
                            <div className='w-1 h-1 bg-gray-700 rounded-full'></div>
                            <span className='text-sm'>{item}</span>
                          </li>
                        );
                      }
                    })}
                  </ul>
                )}

                <div className='flex items-center gap-2 mb-1'>
                  <MdLockOutline className='text-[#79b1d3]' />
                  <span className='md:text-sm text-xs'>
                    All Transactions are 100% Secure
                  </span>
                </div>
                <div className='flex items-center gap-2 mb-4'>
                  <MdOutlineTimer className='text-[#79b1d3]' />
                  <span className='md:text-sm text-xs'>No Delay</span>
                </div>

                <div className='flex w-full gap-1 lg:flex-row flex-col justify-between items-center mb-4'>
                  <div
                    className={`flex space-x-8  md:w-[50%] sm:w-[50%] justify-evenly items-center border border-gray-400 rounded shadow-md
                  ${handleQuantityBlur ? 'lg:w-[50%]' : 'lg:w-1/2'}`}
                  >
                    <button
                      onClick={handleDecreaseQuantity}
                      className='px-3 py-[5px] flex-1 font-semibold rounded bg-gray-100 text-gray-800 hover:bg-gray-300 transition-colors duration-200'
                    >
                      -
                    </button>
                    {isEditingQuantity ? (
                      <input
                        type='number'
                        value={quantity}
                        onChange={handleQuantityChange}
                        onBlur={handleQuantityBlur}
                        autoFocus
                        className='w-14 text-center text-lg font-medium border border-gray-300 outline-none rounded'
                      />
                    ) : (
                      <span
                        className='mx-auto text-lg font-medium cursor-pointer'
                        onClick={() => setIsEditingQuantity(true)}
                      >
                        {quantity}
                      </span>
                    )}
                    <button
                      onClick={handleIncreaseQuantity}
                      className='px-3 py-[5px] flex-1 font-semibold rounded bg-gray-100 text-gray-800 hover:bg-gray-300 transition-colors duration-200'
                    >
                      +
                    </button>
                  </div>

                  <motion.button
                    className={`border md:w-[50%] mt-5 lg:mt-0 sm:w-[50%] text-gray-600 py-1 px-2 rounded-sm transition-colors flex justify-center items-center ${
                      isFavorite
                        ? 'border-gray-400 hover:border-gray-400'
                        : 'border-gray-500 hover:border-gray-500'
                    }`}
                    onClick={handleWishlist}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <motion.span
                      variants={textVariants}
                      initial='initial'
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 10,
                      }}
                      whileTap='tap'
                      className='flex items-center'
                    >
                      {isFavorite ? (
                        <FaHeart className='text-lg mr-2 text-red-500' />
                      ) : (
                        <BiHeart className='text-lg mr-2' />
                      )}
                    </motion.span>
                    <motion.span
                      variants={textVariants}
                      initial='initial'
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 20,
                      }}
                      whileTap='tap'
                    >
                      {isFavorite ? 'Remove' : 'Save To Wishlist'}
                    </motion.span>
                  </motion.button>
                </div>

                <button
                  onClick={handleAddToCart}
                  className='w-full bg-buttonprimary hover:bg-buttonhoverPrimary text-white py-1 rounded-sm transition-colors'
                  disabled={loading}
                >
                  {loading
                    ? 'Processing...'
                    : isAlreadyInCart
                    ? 'Update Cart'
                    : 'Add To Cart'}
                </button>

                {isAlreadyInCart && (
                  <button
                    onClick={handleRemoveFromCart}
                    className='w-full bg-red-500 hover:bg-red-600 mt-2 text-white py-1 rounded-sm transition-colors'
                    disabled={loading}
                  >
                    {isRemoveLoading ? 'Removing...' : 'Remove From Cart'}
                  </button>
                )}
              </div>
              <div className='space-y-2'>
                {/* Description Accordion */}
                <div className='w-full'>
                  <motion.button
                    onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                    className='w-full text-left p-3 bg-[#d7e2e9] border border-buttonprimary flex justify-between items-center'
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <span className='font-medium'>Description</span>
                    <motion.span
                      animate={{ rotate: isDescriptionOpen ? 180 : 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      <IoIosArrowDown className='transform' />
                    </motion.span>
                  </motion.button>
                  <AnimatePresence>
                    {isDescriptionOpen && (
                      <motion.div
                        key='description'
                        variants={accordionVariants}
                        initial='hidden'
                        animate='visible'
                        exit='hidden'
                        className='p-3 bg-[#d7e2e9] border border-t-0 border-buttonprimary overflow-hidden'
                      >
                        <p className='text-sm text-gray-600'>{description}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Details Accordion */}
                <div className='w-full'>
                  <motion.button
                    onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                    className='w-full text-left p-3 bg-[#d7e2e9] border border-buttonprimary flex justify-between items-center'
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <span className='font-medium'>Details</span>
                    <motion.span
                      animate={{ rotate: isDetailsOpen ? 180 : 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      <IoIosArrowDown className='transform' />
                    </motion.span>
                  </motion.button>
                  <AnimatePresence>
                    {isDetailsOpen && (
                      <motion.div
                        key='details'
                        variants={accordionVariants}
                        initial='hidden'
                        animate='visible'
                        exit='hidden'
                        className='p-3 bg-[#d7e2e9] border border-t-0 border-buttonprimary overflow-hidden'
                      >
                        <p className='text-sm text-gray-600'>{details}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSwapConfirmModal && (
        <SwapConfirmationModal
          onCancel={() => setShowSwapConfirmModal(false)}
          onConfirm={handleSwapConfirm}
        />
      )}
    </>
  );
};

export default ProductDetailsPopupWithCount;
